import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SuspenseLoader from 'src/components/SuspenseLoader';

import { useAuth } from '../../../contexts/AuthContext';
import { get } from '../../../services/api';
import {
  fetchInvoiceById,
  fetchOrders,
  generateBillingItems
} from '../api/billingInvoicingApi';
import {
  BillingItem,
  BillingOrder,
  Invoice,
  useBillingInvoicing
} from '../context/BillingInvoicingContext';

const OrderSelection: React.FC = () => {
  const { t } = useTranslation();
  const {
    selectedCompany,
    setSelectedOrders,
    setBillingItems,
    setCurrentStep,
    fetchAndSetBillingItemsForOrders,
    setInvoice
  } = useBillingInvoicing();
  const { user: _user } = useAuth();
  const [orders, setOrders] = useState<BillingOrder[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [dateFilter, setDateFilter] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [orderInvoices, setOrderInvoices] = useState<Invoice[]>([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
  const [loadingBillingItems, setLoadingBillingItems] = useState(false);

  useEffect(() => {
    if (!selectedCompany) return;
    setLoading(true);
    fetchOrders(selectedCompany.id)
      .then(setOrders)
      .catch(() =>
        setError(
          t('billing.order_selection.error_loading', 'Failed to load orders')
        )
      )
      .finally(() => setLoading(false));
  }, [selectedCompany, t]);

  const filteredOrders = orders.filter((order) => {
    if (statusFilter !== 'ALL' && order.status !== statusFilter) return false;
    if (dateFilter && order.creationDate) {
      return order.creationDate.startsWith(dateFilter);
    }
    return true;
  });

  const handleSelect = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setLoadingBillingItems(true);
    try {
      const selectedOrders = orders.filter((o) => selected.includes(o.id));
      setSelectedOrders(selectedOrders);
      const response = await generateBillingItems(selected);
      let items: BillingItem[] = [];
      if (Array.isArray(response)) {
        items = response as BillingItem[];
      } else {
        items = Object.values(response as Record<string, BillingItem[]>).flat();
      }
      setBillingItems(items);
      setCurrentStep('billing-item-summary');
    } catch {
      setError(
        t(
          'billing.order_selection.error_generate',
          'Failed to generate billing items'
        )
      );
    } finally {
      setGenerating(false);
      setLoadingBillingItems(false);
    }
  };

  const handleLoadExisting = async () => {
    setLoadingBillingItems(true);
    const selectedOrdersList = orders.filter((o) => selected.includes(o.id));
    setSelectedOrders(selectedOrdersList);
    await fetchAndSetBillingItemsForOrders(selected);
    setCurrentStep('billing-item-summary');
    setLoadingBillingItems(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleLoadExistingInvoices = async () => {
    if (!selectedCompany || selected.length === 0) return;
    // For simplicity, only support single order selection for invoice viewing
    const orderId = selected[0];
    const invoices = await get(`/v1/invoices?orderId=${orderId}`);
    setOrderInvoices(Array.isArray(invoices) ? invoices : []);
    setInvoiceDialogOpen(true);
  };

  return (
    <Box mt={4} sx={{ width: '100%' }}>
      {(loading || loadingBillingItems) && (
        <SuspenseLoader
          message={t(
            'billing.order_selection.loading_billing_items',
            'Loading billing items...'
          )}
        />
      )}
      <Typography variant="h6" mb={2}>
        {t(
          'billing.order_selection.title',
          'Select Orders for Billing Item Generation'
        )}
      </Typography>
      <Box
        display="flex"
        gap={2}
        mb={2}
        alignItems="center"
        sx={{ width: '100%' }}
      >
        <FormControl fullWidth sx={{ maxWidth: 400 }}>
          <InputLabel>
            {t('billing.order_selection.status', 'Status')}
          </InputLabel>
          <Select
            value={statusFilter}
            label={t('billing.order_selection.status', 'Status')}
            onChange={(e) => setStatusFilter(e.target.value)}
            fullWidth
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="ALL">
              {t('billing.order_selection.status_all', 'All')}
            </MenuItem>
            <MenuItem value="CREATED">
              {t('billing.order_selection.status_created', 'CREATED')}
            </MenuItem>
            <MenuItem value="OFFERED">
              {t('billing.order_selection.status_offered', 'OFFERED')}
            </MenuItem>
            <MenuItem value="CONFIRMED">
              {t('billing.order_selection.status_confirmed', 'CONFIRMED')}
            </MenuItem>
            <MenuItem value="REJECTED">
              {t('billing.order_selection.status_rejected', 'REJECTED')}
            </MenuItem>
            <MenuItem value="CANCELLED">
              {t('billing.order_selection.status_cancelled', 'CANCELLED')}
            </MenuItem>
            <MenuItem value="OPERATIONAL">
              {t('billing.order_selection.status_operational', 'OPERATIONAL')}
            </MenuItem>
            <MenuItem value="COMPLETED">
              {t('billing.order_selection.status_completed', 'COMPLETED')}
            </MenuItem>
          </Select>
        </FormControl>
        <TextField
          label={t('billing.order_selection.order_date', 'Order Date')}
          type="date"
          InputLabelProps={{ shrink: true }}
          value={dateFilter || ''}
          onChange={(e) => setDateFilter(e.target.value || null)}
          fullWidth
          sx={{ maxWidth: 300 }}
        />
        <Box display="flex" alignItems="center" ml={2}>
          <InfoOutlinedIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
          <Typography
            variant="body2"
            color="textSecondary"
            dangerouslySetInnerHTML={{
              __html: t(
                'billing.order_selection.info',
                'Only orders with status <b>COMPLETED</b> can be selected for billing item generation.',
                { interpolation: { escapeValue: false } }
              )
            }}
          />
        </Box>
      </Box>
      {loading ? (
        <Box display="flex" justifyContent="center" my={2}>
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <TableContainer sx={{ width: '100%' }}>
          <Table size="small" sx={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox"></TableCell>
                <TableCell>
                  {t(
                    'billing.order_selection.table.order_number',
                    'Order Number'
                  )}
                </TableCell>
                <TableCell>
                  {t('billing.order_selection.table.status', 'Status')}
                </TableCell>
                <TableCell>
                  {t('billing.order_selection.table.date', 'Date')}
                </TableCell>
                <TableCell>
                  {t('billing.order_selection.table.amount', 'Amount')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders.map((order) => {
                const isCompleted = order.status === 'COMPLETED';
                return (
                  <TableRow
                    key={order.id}
                    hover
                    sx={
                      !isCompleted
                        ? { backgroundColor: '#f5f5f5', opacity: 0.7 }
                        : {}
                    }
                  >
                    <TableCell padding="checkbox">
                      {isCompleted ? (
                        <Checkbox
                          checked={selected.includes(order.id)}
                          onChange={() => handleSelect(order.id)}
                          inputProps={{
                            'aria-label': 'select completed order'
                          }}
                        />
                      ) : (
                        <Tooltip title="Only completed orders can be selected for billing item generation.">
                          <span>
                            <Checkbox
                              checked={false}
                              disabled
                              inputProps={{
                                'aria-label': 'only completed orders selectable'
                              }}
                            />
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {order.requestReferenceId || order.id}
                    </TableCell>
                    <TableCell>
                      {t(
                        `billing.order_selection.status_${order.status.toLowerCase()}`,
                        order.status
                      )}
                    </TableCell>
                    <TableCell>
                      {order.status === 'COMPLETED' && order.creationDate
                        ? format(new Date(order.creationDate), 'yyyy-MM-dd')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {order.amount != null
                        ? `€${order.amount.toFixed(2)}`
                        : '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Box display="flex" gap={2} mt={2} sx={{ width: '100%' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerate}
          disabled={selected.length === 0 || generating}
        >
          {generating ? (
            <CircularProgress size={20} />
          ) : (
            t('billing.order_selection.generate', 'Generate Billing Items')
          )}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleLoadExisting}
          disabled={selected.length === 0 || generating}
        >
          {t('billing.order_selection.load_existing')}
        </Button>
      </Box>
      <Dialog
        open={invoiceDialogOpen}
        onClose={() => setInvoiceDialogOpen(false)}
      >
        <DialogTitle>{t('Select Existing Invoice')}</DialogTitle>
        <DialogContent>
          <TextField
            select
            label={t('Invoice')}
            value={selectedInvoiceId}
            onChange={(e) => setSelectedInvoiceId(e.target.value)}
            fullWidth
            margin="normal"
          >
            {orderInvoices.map((inv) => (
              <MenuItem
                key={inv.invoiceId || inv.id}
                value={inv.invoiceId || inv.id}
              >
                {`#${inv.invoiceNumber || inv.invoiceId || inv.id} | ${
                  inv.invoiceDate || ''
                } | €${
                  inv.grossTotal != null ? inv.grossTotal.toFixed(2) : '-'
                }`}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceDialogOpen(false)}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!selectedInvoiceId}
            onClick={async () => {
              if (!selectedCompany) return;
              const invoice = await fetchInvoiceById(
                selectedCompany.id,
                selectedInvoiceId
              );
              setInvoice(invoice);
              setInvoiceDialogOpen(false);
              setCurrentStep('invoice-details');
            }}
          >
            {t('View Invoice')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderSelection;
